import React, { Component } from 'react';
import _ from 'underscore';
import * as hashUtils from './hashUtils.jsx';
import * as uuid from 'uuid';
import { Checkbox } from 'semantic-ui-react';
import AnimateHeight from 'react-animate-height';
import Field from '../field/Field.jsx';
import EventEmitter from '../../util/eventEmitter.js';

export default class HashFormItem extends Component {
  constructor(props) {
    super(props);

    this.animationDuration = process.env.NODE_ENV === 'test' ? 0 : 500;
    this.state = {
      open: this.getDefaultOpen(props, props.formEnabled),
      childrenFormToSubmit: false,
      keys: {},
    };

    this.animatedIndex = false;
    this.listen();

    this.openInvalidFields();
  }

  openInvalidFields() {
    if (!_.isEmpty(this.props.errors)) {
      this.openInvalidOrRequiredEmpty();
    }
  }

  listen() {
    EventEmitter.on('hashFormSubmit', () => this.openInvalidOrRequiredEmpty());
  }

  componentWillUnmount() {
    EventEmitter.off('hashFormSubmit', () => this.openInvalidOrRequiredEmpty());
  }

  valueFor(keyIndex, index) {
    let values,
      stateValue = this.props.template.key.multiple
        ? this.props.value.value[keyIndex]
        : this.props.value.value;

    if (!hashUtils.isHashType(this.props.template.value)) {
      return stateValue;
    }

    values = typeof stateValue === 'undefined' ? [] : stateValue;
    if (typeof values[index] === 'undefined') {
      return;
    }
    return values[index];
  }

  componentDidUpdate() {
    if (this.repeatedKeys.length > 0) {
      this.handleError();
    }
  }

  // Collapsable subhashes must be opened by default if the form is enabled and:
  // 1 - There's any error
  // 2 - There's required subitems if not at the root level
  getDefaultOpen(props, formEnabled) {
    if (!formEnabled) {
      return false;
    }
    if (props.level === 0) {
      return false;
    }
    if (this.anyRequired(props.template)) {
      return true;
    }
    return false;
  }

  // This method is called when the submit event is triggered in the form
  openInvalidOrRequiredEmpty() {
    if (
      this.hasRequiredEmpty(
        this.props.value,
        this.props.template,
        this.props.level,
      )
    ) {
      let keys = Array.isArray(this.props.value.key)
        ? this.props.value.key
        : [this.props.value.key];
      keys.forEach((key) => {
        if (_.isObject(key)) {
          this.setKeyOpen(key.id);
        }
      });

      this.setState({ childrenFormToSubmit: true });
    }
  }

  hasRequiredEmpty(valueHash, template, level) {
    if (this.isEnabled(valueHash) && hashUtils.isHashType(template.value)) {
      let value = template.key.multiple ? valueHash.value : [valueHash.value]; // standardize to value format when key is multiple

      return value.some((v) => {
        return template.children.some((childTemplate, idx) =>
          this.hasRequiredEmpty(v[idx], childTemplate, level + 1),
        );
      });
    }
    return this.isEnabled(valueHash) && template.required
      ? this.isValueEmpty(valueHash.value) || this.hasError(level, valueHash)
      : false;
  }

  isValueEmpty(value) {
    if (value === null || typeof value === 'undefined') {
      return true;
    }
    if (typeof value === 'string') {
      return _.isEmpty(value);
    }
    if (typeof value === 'boolean' || typeof value === 'number') {
      return false;
    }
    return !!value;
  }

  anyRequired(template) {
    return (
      (template.required && template.value.types.indexOf('hash') === -1) ||
      template.children.some((temp) => this.anyRequired(temp))
    );
  }

  hasErrorOnItem(level, key, value) {
    let levelHasError = Object.keys(this.props.errors).some(
      (error) => error.indexOf(`${level}:${key.value}`) === 0,
    );

    if (!Array.isArray(value)) {
      return levelHasError;
    }
    return (
      levelHasError ||
      value.some((childValue) => this.hasError(level + 1, childValue))
    );
  }

  // Searches for errors in the current key and in child keys in a recursive way
  hasError(level, valueObj) {
    let levelHasError;

    if (typeof valueObj !== 'object') {
      return false;
    }

    if (Array.isArray(valueObj.key)) {
      levelHasError = valueObj.key.some((key, index) =>
        this.hasErrorOnItem(level, key, valueObj.value[index]),
      );
    } else {
      levelHasError = this.hasErrorOnItem(level, valueObj.key, valueObj.value);
    }

    return levelHasError;
  }

  hasChildren() {
    return this.props.template.children.length > 0;
  }

  isHashOptional() {
    return (
      hashUtils.isHashType(this.props.template.value) &&
      !this.props.template.required
    );
  }

  hasMultipleKeys() {
    return this.props.template.key.multiple === true;
  }

  metadataForItem(value, attr) {
    if (attr === 'key') {
      value = value.value;
    }
    if (_.isEmpty(value)) {
      return {};
    }
    if (value.charAt(0) !== '$') {
      return {};
    }
    return { reference: value.replace('$', '') };
  }

  metadataForValue(value, attr) {
    if (Array.isArray(value)) {
      return value.map((i) => this.metadataForValue(i, attr));
    }
    return this.metadataForItem(value, attr);
  }

  getMetadataFor(attr, index = false) {
    let value = this.props.value[attr];

    if (Array.isArray(value) && index !== false) {
      value = this.metadataForValue(value[index], attr);
    } else {
      value = this.metadataForValue(value, attr);
    }

    return value;
  }

  isReference(value, references) {
    return references.find((i) => `$${i.value}` === value);
  }

  referenceOptions(attr) {
    let options = [];

    this.props.template[attr].types.map((type) => {
      this.props.properties[type].map((option) => options.push(option));
    });
    return options;
  }

  updateState(value) {
    this.props.onChange(
      { ...value, id: this.props.template.id },
      this.props.keyIndex,
      this.props.index,
    );
  }

  handleError() {
    this.props.onError();
  }

  valueOrReference(value) {
    if (
      typeof value.metadata !== 'undefined' &&
      value.metadata !== null &&
      typeof value.metadata.reference !== 'undefined' &&
      !(
        _.isEmpty(value.metadata.reference) &&
        !_.isNumber(value.metadata.reference)
      )
    ) {
      return `$${value.metadata.reference}`;
    }

    return value.value;
  }

  valueChanged(keyIndex, e) {
    let values = this.props.value.value,
      finalValue;

    if (this.props.template.key.multiple) {
      values[keyIndex] = !this.props.template.value.multiple
        ? this.valueOrReference(e)
        : e.value.map((value, index) => {
            return this.valueOrReference({
              value: value,
              metadata: e.metadata[index],
            });
          });
      finalValue = values;
    } else {
      finalValue = !this.props.template.value.multiple
        ? this.valueOrReference(e)
        : e.value.map((value, index) => {
            return this.valueOrReference({
              value: value,
              metadata: e.metadata[index],
            });
          });
    }

    this.updateState({
      key: this.props.value.key,
      enabled: this.props.value.enabled,
      value: finalValue,
    });
  }

  keyChanged(index, key) {
    let keys = this.props.value.key,
      keyValue;

    if (this.props.template.key.multiple) {
      keys[index].value = this.valueOrReference(key);
      keyValue = keys;
    } else {
      keyValue = { id: keys.id, value: this.valueOrReference(key) };
    }

    this.updateState({
      value: this.props.value.value,
      enabled: this.props.value.enabled,
      key: keyValue,
    });
  }

  handleChange(item, keyIndex, index) {
    let values = this.props.value.value;

    if (this.props.template.key.multiple) {
      values[keyIndex][index] = item;
    } else {
      values[index] = item;
    }

    this.updateState({
      key: this.props.value.key,
      enabled: this.props.value.enabled,
      value: values,
    });
  }

  handleSwitchChange(checked) {
    this.updateState({
      key: this.props.value.key,
      value: this.props.value.value,
      enabled: checked,
    });
  }

  isEnabled(value = this.props.value) {
    const enabled = typeof value.enabled === 'undefined' ? true : value.enabled;

    return !this.props.parentDisabled && enabled;
  }

  shouldAnimate(index) {
    return index === this.animatedIndex;
  }

  addKey() {
    const {
      template: { children },
      value: { key, value, enabled },
    } = this.props;

    this.animatedIndex = key.length;
    this.updateState({
      key: [...key, { value: '', id: uuid.v4() }],
      value: [...value, hashUtils.parseValueItem([], children)],
      enabled,
    });
  }

  isKeyOpen(keyId) {
    let key = this.state.keys[keyId];

    if (key === undefined) {
      return this.getDefaultOpen(this.props, this.props.formEnabled);
    }

    return key;
  }

  removeKey(index, keyValueId) {
    let keys = this.props.value.key,
      values = this.props.value.value,
      stateKeys = this.state.keys;

    keys.splice(index, 1);
    values.splice(index, 1);
    delete stateKeys[keyValueId];

    this.updateState({
      value: values,
      key: keys,
      enabled: this.props.value.enabled,
    });
    this.setState({ keys: stateKeys });
  }

  classNames(keyIndex) {
    let classNames = [];

    if (this.props.template.required) {
      classNames.push('Required');
    }
    if (hashUtils.isHashType(this.props.template.value)) {
      classNames.push('HashValue');
    }
    if (this.hasMultipleKeys()) {
      classNames.push('MultipleKeys');
    }
    if (keyIndex === this.props.value.key.length - 1) {
      classNames.push('animate__animated animate__fadeIn');
    }
    if (this.props.parentDisabled) {
      classNames.push('parent-disabled');
    }

    if (
      !this.hasMultipleKeys() &&
      this.isHashOptional() &&
      !this.props.parentDisabled
    ) {
      classNames.push('hasSwitch');
    }

    if (!hashUtils.isHashType(this.props.template.value)) {
      classNames.push('ui grid');
    }

    return classNames.join(' ');
  }

  valueClassNames() {
    let classNames = ['HashValue'],
      children = this.props.template.children;

    if (!hashUtils.isHashType(this.props.template.value)) {
      classNames.push('eight wide column');
    }
    if (this.hasChildren()) {
      classNames.push('HasChildren');
    }
    if (children.length > 0 && _.first(children).key.multiple) {
      classNames.push('ChildrenIteration');
    }

    return classNames.join(' ');
  }

  containerClassNames() {
    let classNames = ['HashFormItemContainer'];

    if (this.hasMultipleKeys()) {
      classNames.push('multiple');
    }
    if (this.hasMultipleKeys() && this.props.value.key.length === 0) {
      classNames.push('empty');
    }
    if (this.props.template.value.multiple === true) {
      classNames.push('multipleValues');
    }
    if (this.props.template.value.types.indexOf('hash') === -1) {
      classNames.push('valueNotHash');
    }

    classNames.push(this.props.formEnabled ? 'enabled' : 'disabled');

    return classNames.join(' ');
  }

  shouldShowLegend(key) {
    return !(_.isEmpty(key) || key === `$${this.props.initialReference}`);
  }

  keyLabel(key) {
    let keyLabel;

    if (key.value.charAt(0) !== '$') {
      return key.value;
    }
    keyLabel = this.referenceOptions('key')
      .filter((item) => `$${item.value}` === key.value)
      .map((item) => {
        let label = item.label;

        if (!_.isEmpty(item.customLabel)) {
          label += ` (${item.customLabel})`;
        }
        return label;
      });

    return _.first(keyLabel);
  }

  renderDescription() {
    if (_.isEmpty(this.props.template.description)) {
      return;
    }
    return (
      <p
        key='desc'
        className='HashFormItemDescription help'
      >
        {this.props.template.description}
      </p>
    );
  }

  getRepeatedKeys() {
    if (Array.isArray(this.props.value.key)) {
      let processed = [];

      return this.props.value.key.reduce((duplicates, element) => {
        if (duplicates.includes(element.value)) {
          return duplicates;
        }
        if (processed.includes(element.value)) {
          duplicates.push(element.value);
        } else {
          processed.push(element.value);
        }
        return duplicates;
      }, []);
    }
    return [];
  }

  escapeForError(str) {
    if (typeof str !== 'string') {
      return str;
    }
    return str.replace(/:/g, '::');
  }

  findKeyError(key) {
    let message =
      this.props.errors[
        `${this.props.level}:${this.escapeForError(key.value)}`
      ];

    if (
      typeof message === 'undefined' &&
      this.repeatedKeys.includes(key.value)
    ) {
      message = ['The values for Keys in this level should be unique.'];
    }

    return { 0: message };
  }

  findValueErrors(key, value) {
    if (!Array.isArray(value)) {
      value = [value];
    }

    let errors = {};

    value.forEach((v, i) => {
      let error =
        this.props.errors[
          `${this.props.level}:${this.escapeForError(key.value)}:${this.escapeForError(String(v))}`
        ];

      if (typeof error !== 'undefined') {
        errors[i] = error;
      }
    });

    return errors;
  }

  getHeight(key) {
    return this.isKeyOpen(key) ? 'auto' : 0;
  }

  toggleHeight(valueId) {
    let keys = this.state.keys;

    keys[valueId] = !keys[valueId];
    this.props.heightChanged();
    this.setState({ keys: keys });
  }

  setKeyOpen(keyId) {
    let keys = this.state.keys;

    keys[keyId] = true;

    this.props.heightChanged();
    this.setState({ keys: keys });
  }

  renderToggle(keyId) {
    return (
      <If condition={hashUtils.isHashType(this.props.template.value)}>
        <a
          key='toggle'
          className='hashToggle'
          onClick={() => this.toggleHeight(keyId)}
        >
          <i
            className={
              this.isKeyOpen(keyId) ? 'icon chevron up' : 'icon chevron down'
            }
          />
        </a>
      </If>
    );
  }

  renderKey(key, keyIndex) {
    let keyValue = hashUtils.getValueOf(this.props.value.key, keyIndex),
      classNames = ['HashKey'];

    if (!hashUtils.isHashType(this.props.template.value)) {
      classNames.push('eight wide column');
    }
    if (this.referenceOptions('key').length === 0) {
      classNames.push('noReference');
    }
    if (this.props.template.key.mutable) {
      classNames.push('mutable');
    }
    if (this.props.template.required) {
      classNames.push('required');
    }

    return (
      <div className={classNames.join(' ')}>
        <If condition={!this.props.template.key.mutable}>
          <label className='staticHashKey'>
            {_.isEmpty(this.props.template.label)
              ? this.props.template.key.default
              : this.props.template.label}
            :
          </label>
          {this.renderToggle(key.id)}
        </If>
        {this.renderKeyError(key)}
        <If
          condition={
            !this.hasMultipleKeys() &&
            this.isHashOptional() &&
            !this.props.parentDisabled
          }
        >
          {this.renderSwitch()}
        </If>
        <If condition={this.props.template.key.mutable}>
          <Field
            key={keyValue.id}
            errors={this.findKeyError(keyValue)}
            fixed={true}
            index={keyIndex}
            initialReference={this.props.initialReference}
            label={''}
            metadata={this.getMetadataFor('key', keyIndex)}
            multiple={false}
            outside={true}
            parentDisabled={this.props.parentDisabled}
            placeholder={hashUtils.getLabelFor(this.props.template.key) || ''}
            references={this.referenceOptions('key')}
            required={this.props.template.required}
            semantic={true}
            type={hashUtils.getTypeForComponent(this.props.template.key)}
            value={keyValue.value}
            onChange={this.keyChanged.bind(this)}
          />
          {this.renderToggle(key.id)}
        </If>
        {this.renderDescription()}
        <If
          condition={
            this.props.template.key.multiple &&
            !this.props.parentDisabled &&
            (!this.props.parentIsRequired || this.props.value.value.length > 1)
          }
        >
          <a
            className='ui red button'
            onClick={() => this.removeKey(keyIndex, keyValue.id)}
          >
            <i className='icon minus circle'></i>
            Remove Item
          </a>
        </If>
      </div>
    );
  }

  renderValue(keyIndex) {
    let keyValue = hashUtils.getValueOf(this.props.value.key, keyIndex),
      argumentKey = this.props.template.key.multiple ? keyIndex : false,
      value = hashUtils.getValueOf(this.props.value.value, argumentKey),
      metadata = this.getMetadataFor('value', argumentKey);

    return (
      <div className='HashValueField'>
        <If condition={!this.props.template.value.mutable}>
          <label className='staticHashValue'>
            {this.props.template.value.default}
          </label>
        </If>
        <If condition={this.props.template.value.mutable}>
          <Field
            key={keyValue.id}
            definitionTypes={this.props.template.value.types}
            errors={this.findValueErrors(keyValue, value)}
            fixed={true}
            index={keyIndex}
            initialReference={this.props.initialReference}
            label={'Add new value'}
            metadata={metadata}
            multiple={this.props.template.value.multiple}
            outside={true}
            parentDisabled={this.props.parentDisabled}
            placeholder={hashUtils.getLabelFor(this.props.template.value) || ''}
            references={this.referenceOptions('value')}
            required={this.props.template.required}
            semantic={true}
            type={hashUtils.getTypeForComponent(this.props.template.value)}
            value={value}
            onChange={this.valueChanged.bind(this)}
          />
        </If>
      </div>
    );
  }

  renderChildren(keyIndex) {
    return (
      <div className='HashValueChildren'>
        {this.props.template.children.map((item, index) => {
          return (
            <HashFormItem
              key={item.id}
              animate={this.shouldAnimate(index)}
              errors={this.props.errors}
              formEnabled={this.props.formEnabled}
              formToSubmit={
                this.props.formToSubmit && this.state.childrenFormToSubmit
              }
              heightChanged={this.props.heightChanged}
              index={index}
              initialReference={this.props.initialReference}
              keyIndex={keyIndex}
              level={this.props.level + 1}
              parentDisabled={!this.isEnabled()}
              parentIsRequired={!this.isHashOptional()}
              properties={this.props.properties}
              propertyLabels={this.props.propertyLabels}
              template={item}
              value={this.valueFor(keyIndex, index)}
              onChange={this.handleChange.bind(this)}
              onError={() => this.handleError()}
            />
          );
        })}
      </div>
    );
  }

  renderCollapsable(keyIndex, keyId) {
    return (
      <AnimateHeight
        key={keyId}
        duration={this.animationDuration}
        height={this.getHeight(keyId)}
      >
        <If condition={!this.hasMultipleKeys()}>
          {this.renderChildren(keyIndex)}
        </If>
        <If condition={this.hasMultipleKeys()}>
          <fieldset>
            <If
              condition={this.shouldShowLegend(this.props.value.key[keyIndex])}
            >
              <legend>{this.keyLabel(this.props.value.key[keyIndex])}</legend>
            </If>
            {this.renderChildren(keyIndex)}
          </fieldset>
        </If>
      </AnimateHeight>
    );
  }

  renderItem(key = this.props.value.key, keyIndex = 0) {
    return (
      <div
        key={keyIndex}
        className={`HashFormItem ${this.classNames(keyIndex)}`}
        style={{ zIndex: this.props.zIndex }}
      >
        {this.renderKey(key, keyIndex)}
        <div className={this.valueClassNames()}>
          <If condition={!hashUtils.isHashType(this.props.template.value)}>
            {this.renderValue(keyIndex)}
          </If>
          {this.hasChildren
            ? this.renderCollapsable(keyIndex, key.id)
            : undefined}
        </div>
      </div>
    );
  }

  renderSwitch() {
    return (
      <div className={'HashSwitch hide-disabled'}>
        <Checkbox
          toggle
          defaultChecked={this.props.value.enabled}
          label={'Enable this option'}
          onChange={(e, { checked }) => this.handleSwitchChange(checked)}
        />
      </div>
    );
  }

  render() {
    this.repeatedKeys = this.getRepeatedKeys();

    return (
      <div className={this.containerClassNames()}>
        <If condition={!this.hasMultipleKeys()}>{this.renderItem()}</If>
        <If condition={this.hasMultipleKeys()}>
          <div className='MultipleKeyFormItem'>
            {this.props.value.key.map((item, index) => {
              return this.renderItem(item, index);
            })}
          </div>
          <If condition={!this.props.parentDisabled}>
            <div className='MultipleKeyAdd'>
              <a
                className='ui primary button'
                onClick={this.addKey.bind(this)}
              >
                <i className='icon plus circle'></i> Add new value
              </a>
            </div>
          </If>
        </If>
      </div>
    );
  }

  renderKeyError(key) {
    let message =
      this.props.errors[
        `${this.props.level}:${this.escapeForError(key.value)}`
      ];

    if (typeof message === 'undefined') {
      return;
    }
    return <div className='error'>{message}</div>;
  }
}
